/**
 * useAffiliateImportPreview — preview server-side (até 50 itens) de um job de
 * importação de afiliados. E-mail, telefone e documento mascarados no banco.
 *
 * RELEASE:
 *   2026-05-17 v1.1.0 - Adiciona program_name e error_message.
 *   2026-05-17 v1.0.0 - Versão inicial.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useAffiliateImportPreview');

export interface AffiliateImportPreviewItem {
  id: string;
  row_index: number;
  name: string | null;
  email_masked: string | null;
  phone_masked: string | null;
  document_masked: string | null;
  program_name: string | null;
  status: string;
  validation_errors: unknown;
  error_message: string | null;
}

export function useAffiliateImportPreview(jobId: string | null, enabled: boolean) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['affiliate-import-preview', currentTenant?.id, jobId],
    enabled: enabled && !!jobId && !!currentTenant?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<AffiliateImportPreviewItem[]> => {
      const { data, error } = await (supabase.rpc as any)('get_affiliate_import_preview', {
        p_job_id: jobId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) {
        logger.error('Erro ao carregar preview de importação de afiliados', error);
        throw error;
      }
      return (data ?? []) as AffiliateImportPreviewItem[];
    },
  });
}
